import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import {
  normalizeBuyInValue,
  ROOM_THEME,
  sekaPlayerModes,
  pokerPlayerModes,
  belotPlayerModes,
  BUCKS_IMG,
  SEKA_ROOM_ANTE_OPTIONS,
} from './LobbyTablesBlock';

const defaultFormForMode = (gameMode) => ({
  name: '',
  currency: 'seka',
  maxPlayers: gameMode === 'poker' ? 6 : gameMode === 'belot' ? 4 : 2,
  buyIn: 50,
  ante: 10,
  belotVariant: 'armenian',
  belotTargetScore: 10010,
  belotShulerEnabled: false,
  isPrivate: false,
  roomPassword: '',
});

/**
 * Единое модальное окно «Создание комнаты» (как в OnlineLobby).
 * @param {(payload: {
 *   name: string;
 *   currency: string;
 *   maxPlayers: number;
 *   buyIn: number;
 *   ante?: number;
 *   belotVariant?: string;
 *   belotTargetScore?: 5010|10010;
 *   isPrivate: boolean;
 *   roomPassword: string;
 * }) => void} onConfirm
 */
export function OnlineStyleCreateRoomModal({
  open,
  onClose,
  mode = 'seka',
  user,
  connected = false,
  submitting = false,
  externalError = '',
  onClearExternalError,
  preset = null,
  onPresetApplied,
  onConfirm,
}) {
  const roomTheme = ROOM_THEME[mode] || ROOM_THEME.seka;
  const isBelotMode = mode === 'belot';
  const isPokerMode = mode === 'poker';
  const isSekaMode = mode === 'seka';

  const [createForm, setCreateForm] = useState(() => defaultFormForMode(mode));
  const [validationError, setValidationError] = useState('');

  const isBucksCurrency = createForm.currency === 'bucks';
  const currencyLabel = isBucksCurrency ? '€' : 'ФИШКИ';
  const sekaBalance = Math.max(0, Number(user?.balance ?? 0));
  const bucksBalance = Math.max(
    0,
    Number(user?.bucksBalance ?? user?.realBalance ?? user?.cashBalance ?? 0)
  );
  const activeBalance = isBucksCurrency ? bucksBalance : sekaBalance;

  const buyInConfig = useMemo(() => {
    const baseBuyInConfig = isBucksCurrency
      ? { min: 0.5, max: 10000, step: 0.5 }
      : { min: 10, max: 1000000, step: 10 };
    const roundedBalanceByStep =
      Math.floor(activeBalance / baseBuyInConfig.step) * baseBuyInConfig.step;
    const canAffordMin = activeBalance >= baseBuyInConfig.min;
    return {
      ...baseBuyInConfig,
      canAffordMin,
      max: canAffordMin
        ? Math.max(baseBuyInConfig.min, Math.min(baseBuyInConfig.max, roundedBalanceByStep))
        : baseBuyInConfig.min,
    };
  }, [isBucksCurrency, activeBalance]);

  const canAffordMin = buyInConfig.canAffordMin;
  const formattedBuyIn = isBucksCurrency
    ? Number(createForm.buyIn || 0).toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : Number(createForm.buyIn || 0).toLocaleString('ru-RU');
  const formattedActiveBalance = isBucksCurrency
    ? activeBalance.toLocaleString('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : activeBalance.toLocaleString('ru-RU');

  const buyInProgressPercent = Math.max(
    0,
    Math.min(
      100,
      ((Number(createForm.buyIn || buyInConfig.min) - buyInConfig.min) /
        (buyInConfig.max - buyInConfig.min || 1)) *
        100
    )
  );

  const modalAnteMaxIdx = useMemo(() => {
    if (!isSekaMode) return 0;
    const cap = Number(createForm.buyIn) || 0;
    let idx = 0;
    for (let i = 0; i < SEKA_ROOM_ANTE_OPTIONS.length; i++) {
      if (SEKA_ROOM_ANTE_OPTIONS[i] <= cap) idx = i;
      else break;
    }
    return idx;
  }, [isSekaMode, createForm.buyIn]);

  const modalAnteSliderIdx = useMemo(() => {
    if (!isSekaMode) return 0;
    const target = Number(createForm.ante) || SEKA_ROOM_ANTE_OPTIONS[0];
    let best = 0;
    for (let i = 0; i <= modalAnteMaxIdx; i++) {
      if (SEKA_ROOM_ANTE_OPTIONS[i] <= target) best = i;
    }
    return best;
  }, [createForm.ante, modalAnteMaxIdx, isSekaMode]);

  const createRoomSummaryLine = useMemo(() => {
    const stakePart = isBucksCurrency ? `Вход ${formattedBuyIn} €` : `Вход ${formattedBuyIn} фишек`;
    const antePart =
      isSekaMode && Number(createForm.ante) > 0
        ? ` · Анте ${Number(createForm.ante).toLocaleString('ru-RU')} ${isBucksCurrency ? '€' : 'фишек'}`
        : '';
    const modes = isBelotMode ? belotPlayerModes : isPokerMode ? pokerPlayerModes : sekaPlayerModes;
    const sel = modes.find((o) => o.value === createForm.maxPlayers);
    const playersPart = sel ? ` · ${sel.title}` : ` · ${createForm.maxPlayers} игроков`;
    const belotPart =
      isBelotMode && createForm.belotVariant
        ? `${
            createForm.belotVariant === 'moldovan'
              ? ' · Молдавский'
              : createForm.belotVariant === 'open_duell'
                ? ' · Open 1×1'
                : ' · Армянский'
          }${Number(createForm.belotTargetScore) === 5010 ? ' · до 501 CP' : ' · до 1001 CP'}`
        : '';
    const privacyPart = createForm.isPrivate ? ' · приватная' : '';
    const shuPart =
      isBelotMode && createForm.maxPlayers !== 2 && createForm.belotShulerEnabled ? ' · SHU' : '';
    return `${stakePart}${antePart}${playersPart}${belotPart}${shuPart}${privacyPart}`;
  }, [
    isBucksCurrency,
    formattedBuyIn,
    isSekaMode,
    createForm.ante,
    createForm.maxPlayers,
    createForm.belotVariant,
    createForm.belotTargetScore,
    createForm.belotShulerEnabled,
    createForm.isPrivate,
    isBelotMode,
    isPokerMode,
  ]);

  const createRoomNameInputRef = useRef(null);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    setCreateForm((prev) => ({
      ...prev,
      maxPlayers: mode === 'poker' ? 6 : mode === 'belot' ? 4 : 2,
      ante: mode === 'seka' ? prev.ante : 10,
      belotVariant: mode === 'belot' ? prev.belotVariant || 'armenian' : 'armenian',
      belotTargetScore: mode === 'belot' ? prev.belotTargetScore || 10010 : 10010,
    }));
  }, [mode]);

  useEffect(() => {
    if (!isBelotMode) return;
    if (createForm.maxPlayers === 2) {
      if (createForm.belotVariant !== 'open_duell') {
        setCreateForm((prev) => ({ ...prev, belotVariant: 'open_duell', belotShulerEnabled: false }));
      }
      return;
    }
    if (createForm.belotVariant === 'open_duell') {
      setCreateForm((prev) => ({ ...prev, maxPlayers: 4, belotVariant: 'armenian', belotShulerEnabled: false }));
      return;
    }
    if (createForm.maxPlayers === 2 || createForm.maxPlayers === 4) return;
    setCreateForm((prev) => ({ ...prev, maxPlayers: 4 }));
  }, [isBelotMode, createForm.maxPlayers, createForm.belotVariant]);

  useEffect(() => {
    if (!isPokerMode) return;
    if ([2, 6, 9].includes(createForm.maxPlayers)) return;
    setCreateForm((prev) => ({ ...prev, maxPlayers: 6 }));
  }, [isPokerMode, createForm.maxPlayers]);

  useEffect(() => {
    if (!isSekaMode) return;
    if (createForm.maxPlayers >= 2 && createForm.maxPlayers <= 6) return;
    setCreateForm((prev) => ({ ...prev, maxPlayers: 2 }));
  }, [isSekaMode, createForm.maxPlayers]);

  useEffect(() => {
    const normalized = normalizeBuyInValue(createForm.buyIn, buyInConfig);
    if (normalized === createForm.buyIn) return;
    setCreateForm((prev) => ({ ...prev, buyIn: normalized }));
  }, [isBucksCurrency, createForm.buyIn, buyInConfig]);

  useEffect(() => {
    if (!isSekaMode) return;
    setCreateForm((p) => {
      const cap = Number(p.buyIn) || 0;
      let maxVal = SEKA_ROOM_ANTE_OPTIONS[0];
      for (const x of SEKA_ROOM_ANTE_OPTIONS) {
        if (x <= cap) maxVal = x;
        else break;
      }
      if (Number(p.ante) <= maxVal) return p;
      return { ...p, ante: maxVal };
    });
  }, [createForm.buyIn, isSekaMode]);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setValidationError('');
      onClearExternalError?.();
      setCreateForm(() => {
        const base = defaultFormForMode(mode);
        if (!preset || typeof preset !== 'object') return base;
        const p = { ...preset };
        if (p.belotTargetScore != null) {
          const ts = Number(p.belotTargetScore);
          p.belotTargetScore = ts === 5010 || ts === 501 ? 5010 : 10010;
        }
        return { ...base, ...p };
      });
      onPresetApplied?.();
    }
    prevOpenRef.current = open;
  }, [open, mode, preset, onPresetApplied, onClearExternalError]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      createRoomNameInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const displayError = validationError || externalError;

  const handleConfirm = useCallback(() => {
    setValidationError('');
    if (!createForm.name.trim()) {
      setValidationError('Введите название комнаты');
      return;
    }
    const rawBuyIn = Number(createForm.buyIn);
    if (!Number.isFinite(rawBuyIn)) {
      setValidationError('Некорректная сумма входа');
      return;
    }
    const normalizedBuyIn = normalizeBuyInValue(rawBuyIn, buyInConfig);
    if (normalizedBuyIn > activeBalance) {
      setValidationError('Недостаточно средств для такого входа');
      return;
    }
    if (!canAffordMin) {
      setValidationError('Недостаточно средств для минимального входа');
      return;
    }
    if (createForm.isPrivate) {
      const pw = createForm.roomPassword.trim();
      if (pw.length < 4) {
        setValidationError('Пароль комнаты: минимум 4 символа');
        return;
      }
      if (pw.length > 128) {
        setValidationError('Пароль комнаты: не длиннее 128 символов');
        return;
      }
    }
    const anteForRoom =
      mode === 'seka'
        ? Math.min(
            Math.max(SEKA_ROOM_ANTE_OPTIONS[0], Math.floor(Number(createForm.ante) || SEKA_ROOM_ANTE_OPTIONS[0])),
            normalizedBuyIn
          )
        : undefined;
    onConfirm?.({
      name: createForm.name.trim(),
      currency: createForm.currency || 'seka',
      maxPlayers: createForm.maxPlayers,
      buyIn: normalizedBuyIn,
      ...(anteForRoom != null ? { ante: anteForRoom } : {}),
      ...(isBelotMode
        ? {
            belotVariant: createForm.belotVariant || 'armenian',
            belotTargetScore:
              Number(createForm.belotTargetScore) === 5010 || Number(createForm.belotTargetScore) === 501
                ? 5010
                : 10010,
            ...(createForm.maxPlayers === 4 && createForm.belotVariant !== 'open_duell'
              ? { belotShulerEnabled: Boolean(createForm.belotShulerEnabled) }
              : { belotShulerEnabled: false }),
          }
        : {}),
      isPrivate: Boolean(createForm.isPrivate),
      roomPassword: createForm.roomPassword.trim(),
    });
  }, [
    createForm,
    buyInConfig,
    activeBalance,
    canAffordMin,
    mode,
    isBelotMode,
    onConfirm,
  ]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 px-2 py-3 backdrop-blur-sm sm:items-center sm:p-4"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="online-create-room-title"
        className={cn(
          'relative max-h-[min(78dvh,680px)] w-full max-w-md overflow-y-auto rounded-xl border border-white/10 bg-gradient-to-b p-2.5 sm:p-3 shadow-2xl ring-1 outline-none',
          roomTheme.shell,
          roomTheme.ring
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <h2 id="online-create-room-title" className="text-sm font-black uppercase tracking-[0.12em] text-white/95">
            Создание комнаты
          </h2>
          <button
            type="button"
            disabled={submitting}
            onClick={() => onClose?.()}
            className="lobby-focus inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-[#b8c9e0] transition hover:bg-white/10 hover:text-white disabled:opacity-40"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <label className="mb-1 block text-[11px] font-medium text-[#8fa3bf]" htmlFor="online-create-room-name">
          Название комнаты
        </label>
        <input
          id="online-create-room-name"
          ref={createRoomNameInputRef}
          type="text"
          value={createForm.name}
          onChange={(e) => {
            setValidationError('');
            setCreateForm({ ...createForm, name: e.target.value });
          }}
          placeholder={`Например, ${roomTheme.label} Elite`}
          autoComplete="off"
          enterKeyHint="done"
          className={cn(
            'lobby-focus mb-1.5 w-full rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-[13px] text-white placeholder:text-white/35 focus:outline-none focus:ring-1',
            roomTheme.inputFocus
          )}
        />

        <div
          className={cn('mb-1.5 flex items-center justify-between gap-2 rounded-lg border px-2 py-1.5', roomTheme.card)}
        >
          <h3 className="text-[11px] font-black uppercase tracking-[0.12em] text-white/95">Параметры стола</h3>
          <img
            src={roomTheme.logoSrc}
            alt=""
            className="h-7 w-auto max-w-[min(120px,44%)] shrink-0 object-contain"
            aria-hidden
            draggable={false}
          />
        </div>

        <p id="online-create-room-currency" className="mb-1 text-[11px] font-medium text-[#8fa3bf]">
          Валюта
        </p>
        <div className="mb-1.5 flex justify-center gap-1.5" role="group" aria-labelledby="online-create-room-currency">
          <button
            type="button"
            onClick={() => setCreateForm({ ...createForm, currency: 'seka' })}
            aria-label="Валюта: фишки"
            className={cn(
              'lobby-focus inline-flex h-[44px] w-[52px] min-h-[44px] items-center justify-center rounded-lg border p-0 text-xs font-bold align-middle transition',
              createForm.currency === 'seka'
                ? roomTheme.toggleOn
                : 'border-white/15 bg-white/5 text-white/70 hover:bg-white/10'
            )}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/fiski.png`}
              alt=""
              width={38}
              height={38}
              className={cn(
                'h-[38px] w-[38px] shrink-0 object-contain transition duration-200',
                createForm.currency === 'seka'
                  ? 'drop-shadow-[0_0_12px_rgba(251,191,36,0.45)]'
                  : 'opacity-45 brightness-[0.7] saturate-[0.55] drop-shadow-none'
              )}
              draggable={false}
              aria-hidden
            />
          </button>
          <button
            type="button"
            onClick={() => setCreateForm({ ...createForm, currency: 'bucks' })}
            aria-label="Валюта: евро"
            className={cn(
              'lobby-focus inline-flex h-[44px] w-[52px] min-h-[44px] items-center justify-center rounded-lg border p-0 text-xs font-bold align-middle transition',
              createForm.currency === 'bucks'
                ? roomTheme.toggleOn
                : 'border-white/15 bg-white/5 text-white/70 hover:bg-white/10'
            )}
          >
            <img
              src={BUCKS_IMG}
              alt=""
              width={38}
              height={38}
              className={cn(
                'h-[38px] w-[38px] shrink-0 object-contain transition duration-200',
                createForm.currency === 'bucks'
                  ? 'drop-shadow-[0_0_12px_rgba(16,185,129,0.45)]'
                  : 'opacity-45 brightness-[0.7] saturate-[0.55] drop-shadow-none'
              )}
              draggable={false}
              aria-hidden
            />
          </button>
        </div>

        <div className={cn('mb-1.5 rounded-lg border bg-black/25 px-2 py-1.5', roomTheme.card)}>
          <div className="mb-1.5 flex items-end justify-between gap-2">
            <span className="text-[11px] font-medium text-[#8fa3bf]">Вход</span>
            <span
              className={cn(
                'font-mono text-[13px] font-black tabular-nums tracking-tight',
                isBucksCurrency ? 'text-emerald-300' : 'text-[#e8c547]'
              )}
            >
              {formattedBuyIn} {currencyLabel}
            </span>
          </div>
          <input
            type="range"
            min={buyInConfig.min}
            max={buyInConfig.max}
            step={buyInConfig.step}
            value={createForm.buyIn}
            onChange={(e) =>
              setCreateForm({
                ...createForm,
                buyIn: normalizeBuyInValue(Number(e.target.value), buyInConfig),
              })
            }
            disabled={!canAffordMin}
            className={cn(
              'w-full cursor-pointer appearance-none rounded-full',
              'h-1.5 accent-current disabled:cursor-not-allowed disabled:opacity-40',
              roomTheme.accentText
            )}
            style={{
              background: `linear-gradient(to right, ${roomTheme.sliderFill} 0%, ${roomTheme.sliderFill} ${buyInProgressPercent}%, rgba(255,255,255,0.14) ${buyInProgressPercent}%, rgba(255,255,255,0.1) 100%)`,
            }}
            aria-label="Размер входа в комнату"
          />
          <div className="mt-0.5 flex items-center justify-between text-[8px] font-semibold uppercase tracking-wide text-white/45">
            <span>
              Мин{' '}
              {isBucksCurrency
                ? buyInConfig.min.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : buyInConfig.min}
            </span>
            <span>
              Макс{' '}
              {isBucksCurrency
                ? buyInConfig.max.toLocaleString('ru-RU', {
                    minimumFractionDigits: buyInConfig.step < 1 ? 2 : 0,
                    maximumFractionDigits: buyInConfig.step < 1 ? 2 : 0,
                  })
                : buyInConfig.max}
            </span>
          </div>
          <p className="mt-0.5 text-[8px] text-white/55">
            Доступно на балансе: <span className="font-bold text-white/80">{formattedActiveBalance}</span>{' '}
            {currencyLabel}
          </p>
          <div className="mt-1">
            <label className="mb-0.5 block text-[9px] font-medium text-[#8fa3bf]">
              Своя сумма ({currencyLabel})
            </label>
            <input
              type="number"
              min={buyInConfig.min}
              max={buyInConfig.max}
              step={buyInConfig.step}
              value={createForm.buyIn}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  buyIn: normalizeBuyInValue(e.target.value, buyInConfig),
                })
              }
              disabled={!canAffordMin}
              className={cn(
                'lobby-focus w-full rounded-lg border border-white/10 bg-black/30 px-2.5 py-1 text-[12px] text-white placeholder:text-white/35 focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-45',
                roomTheme.inputFocus
              )}
            />
          </div>
          {isSekaMode ? (
            <>
              <div className="my-1 border-t border-white/10" />
              <div className="mb-1 flex items-end justify-between gap-2">
                <span className="text-[11px] font-medium text-[#8fa3bf]">Анте за раздачу</span>
                <span
                  className={cn(
                    'font-mono text-[12px] font-bold tabular-nums',
                    isBucksCurrency ? 'text-emerald-300' : 'text-[#e8c547]'
                  )}
                >
                  {Number(createForm.ante || SEKA_ROOM_ANTE_OPTIONS[0]).toLocaleString('ru-RU')} {currencyLabel}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={modalAnteMaxIdx}
                step={1}
                value={Math.min(modalAnteSliderIdx, modalAnteMaxIdx)}
                onChange={(e) => {
                  const i = Number(e.target.value);
                  setCreateForm({
                    ...createForm,
                    ante: SEKA_ROOM_ANTE_OPTIONS[Math.min(Math.max(0, i), modalAnteMaxIdx)],
                  });
                }}
                disabled={!canAffordMin || modalAnteMaxIdx <= 0}
                className={cn(
                  'h-1 w-full cursor-pointer appearance-none rounded-full bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-40',
                  roomTheme.accentText
                )}
                style={{
                  background: `linear-gradient(to right, ${roomTheme.sliderFill} 0%, ${roomTheme.sliderFill} ${
                    modalAnteMaxIdx <= 0 ? 100 : (modalAnteSliderIdx / modalAnteMaxIdx) * 100
                  }%, rgba(255,255,255,0.08) ${
                    modalAnteMaxIdx <= 0 ? 100 : (modalAnteSliderIdx / modalAnteMaxIdx) * 100
                  }%, rgba(255,255,255,0.06) 100%)`,
                }}
                aria-label="Размер анте"
              />
              <p className="mt-0.5 text-[8px] text-white/50">Не больше выбранного входа за стол.</p>
            </>
          ) : null}
          {!canAffordMin ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-[10px] font-semibold text-red-300/90">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden />
              Недостаточно средств для минимального входа ({buyInConfig.min} {currencyLabel}).
            </p>
          ) : null}
        </div>

        {isBelotMode ? (
          <div className={cn('mb-1.5 rounded-lg border bg-black/25 px-2 py-1.5', roomTheme.card)}>
            <p className="mb-1 text-[11px] font-medium text-[#8fa3bf]">Игроки за столом</p>
            <div className="grid grid-cols-2 gap-1.5">
              {belotPlayerModes.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCreateForm({ ...createForm, maxPlayers: opt.value })}
                  className={cn(
                    'lobby-focus rounded-lg border px-2 py-1.5 text-center transition active:scale-[0.98]',
                    createForm.maxPlayers === opt.value
                      ? roomTheme.toggleOn
                      : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                  )}
                >
                  <p className="text-[13px] font-black leading-none">{opt.title}</p>
                  <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">
                    {opt.subtitle}
                  </p>
                </button>
              ))}
            </div>
            {createForm.maxPlayers === 2 ? (
              <p className="mt-1.5 text-[9px] leading-snug text-white/50">
                Режим «Шулер» доступен только в комнате на 4 игроков (классический стол).
              </p>
            ) : null}
          </div>
        ) : null}

        {isBelotMode && createForm.maxPlayers !== 2 ? (
          <div className={cn('mb-1.5 rounded-lg border bg-black/25 px-2 py-1.5', roomTheme.card)}>
            <p className="mb-1 text-[11px] font-medium text-[#8fa3bf]">Вариант Белота</p>
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setCreateForm({ ...createForm, belotVariant: 'armenian' })}
                className={cn(
                  'lobby-focus rounded-lg border px-2 py-1.5 text-left transition active:scale-[0.98]',
                  createForm.belotVariant === 'armenian'
                    ? roomTheme.toggleOn
                    : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                )}
              >
                <p className="text-[12px] font-black leading-none">Армянский</p>
                <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">Сложные торги</p>
              </button>
              <button
                type="button"
                onClick={() => setCreateForm({ ...createForm, belotVariant: 'moldovan' })}
                className={cn(
                  'lobby-focus rounded-lg border px-2 py-1.5 text-left transition active:scale-[0.98]',
                  createForm.belotVariant === 'moldovan'
                    ? roomTheme.toggleOn
                    : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                )}
              >
                <p className="text-[12px] font-black leading-none">Молдавский</p>
                <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">
                  Быстрая игра
                </p>
              </button>
            </div>
          </div>
        ) : null}

        {isBelotMode && createForm.maxPlayers === 4 && createForm.belotVariant !== 'open_duell' ? (
          <div className={cn('mb-1.5 rounded-lg border bg-black/25 px-2 py-1.5', roomTheme.card)}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-white/90">Режим «Шулер»</p>
                <p className="mt-0.5 text-[9px] leading-snug text-white/45">
                  Схватить за руку: намеренно нелегальный ход и поимка тапом по карте на столе (+16 тенсов, ложное
                  обвинение −10).
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(createForm.belotShulerEnabled)}
                aria-label={createForm.belotShulerEnabled ? 'Режим Шулер включён' : 'Режим Шулер выключен'}
                onClick={() =>
                  setCreateForm((prev) => ({ ...prev, belotShulerEnabled: !prev.belotShulerEnabled }))
                }
                className={cn(
                  'lobby-focus relative h-8 w-12 shrink-0 rounded-full border-2 transition active:scale-95',
                  createForm.belotShulerEnabled
                    ? 'border-amber-400/70 bg-amber-500/45 shadow-[0_0_14px_rgba(251,191,36,0.25)]'
                    : 'border-white/25 bg-black/50',
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none absolute left-1 top-1 block h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200 ease-out',
                    createForm.belotShulerEnabled ? 'translate-x-5' : 'translate-x-0',
                  )}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        ) : null}

        {isBelotMode ? (
          <div className={cn('mb-1.5 rounded-lg border bg-black/25 px-2 py-1.5', roomTheme.card)}>
            <p className="mb-1 text-[11px] font-medium text-[#8fa3bf]">Длина матча</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => setCreateForm({ ...createForm, belotTargetScore: 5010 })}
                className={cn(
                  'lobby-focus rounded-lg border px-2 py-1.5 text-left transition',
                  Number(createForm.belotTargetScore) === 5010
                    ? roomTheme.toggleOn
                    : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                )}
              >
                <p className="text-[12px] font-black leading-none">501</p>
                <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">Короткий матч</p>
              </button>
              <button
                type="button"
                onClick={() => setCreateForm({ ...createForm, belotTargetScore: 10010 })}
                className={cn(
                  'lobby-focus rounded-lg border px-2 py-1.5 text-left transition',
                  Number(createForm.belotTargetScore) !== 5010
                    ? roomTheme.toggleOn
                    : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                )}
              >
                <p className="text-[12px] font-black leading-none">1001</p>
                <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">Классика</p>
              </button>
            </div>
          </div>
        ) : null}

        <div className="mb-1.5">
          <label className="mb-1 flex cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
            <input
              type="checkbox"
              checked={createForm.isPrivate}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  isPrivate: e.target.checked,
                  roomPassword: e.target.checked ? createForm.roomPassword : '',
                })
              }
              className="lobby-focus h-4 w-4 shrink-0 rounded border-white/20 bg-black/40 text-amber-500 focus:ring-amber-500/40"
            />
            <span className="text-[11px] leading-snug text-[#b8c9e0]">
              <span className="font-medium text-white/90">Приватная комната</span>
              <span className="mt-0.5 block text-[9px] text-white/45">Вход по паролю для остальных игроков</span>
            </span>
          </label>
          {createForm.isPrivate ? (
            <>
              <label className="mb-1 block text-[11px] font-medium text-[#8fa3bf]" htmlFor="online-create-room-password">
                Пароль комнаты
              </label>
              <input
                id="online-create-room-password"
                type="password"
                autoComplete="new-password"
                value={createForm.roomPassword}
                onChange={(e) => setCreateForm({ ...createForm, roomPassword: e.target.value })}
                placeholder="Минимум 4 символа"
                className={cn(
                  'lobby-focus w-full rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-[13px] text-white placeholder:text-white/35 focus:outline-none focus:ring-1',
                  roomTheme.inputFocus
                )}
              />
            </>
          ) : null}
        </div>

        {!isBelotMode ? (
          <>
            <p className="mb-1 text-[11px] font-medium text-[#8fa3bf]">Игроки за столом</p>
            {isSekaMode ? (
              <div className="mb-1.5 grid grid-cols-3 gap-1.5 sm:grid-cols-5">
                {sekaPlayerModes.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, maxPlayers: opt.value })}
                    className={cn(
                      'lobby-focus rounded-lg border px-2 py-1.5 text-center transition active:scale-[0.98]',
                      createForm.maxPlayers === opt.value
                        ? roomTheme.toggleOn
                        : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                    )}
                  >
                    <p className="text-[13px] font-black leading-none">{opt.title}</p>
                    <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">
                      {opt.subtitle}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mb-1.5 grid grid-cols-1 gap-1.5">
                {pokerPlayerModes.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, maxPlayers: opt.value })}
                    className={cn(
                      'lobby-focus rounded-lg border px-2 py-1.5 text-center transition active:scale-[0.98]',
                      createForm.maxPlayers === opt.value
                        ? roomTheme.toggleOn
                        : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                    )}
                  >
                    <p className="text-[13px] font-black leading-none">{opt.title}</p>
                    <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60">
                      {opt.subtitle}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : null}

        {displayError ? (
          <div
            className="mb-1.5 flex items-start gap-1.5 rounded-lg border border-red-500/40 bg-red-950/40 px-2.5 py-1.5 text-[12px] text-red-200"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden />
            <span>{displayError}</span>
          </div>
        ) : null}

        <p
          className="mb-1.5 rounded-lg border border-white/10 bg-black/20 px-2 py-1 text-center text-[10px] leading-snug text-white/65"
          role="status"
        >
          {createRoomSummaryLine}
        </p>

        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            className="lobby-focus flex-1 rounded-lg border border-white/15 py-1.5 text-[13px] font-semibold text-[#b8c9e0] transition duration-150 hover:bg-white/5"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || !canAffordMin || Number(createForm.buyIn) > activeBalance || !connected}
            className={cn(
              'lobby-focus flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gradient-to-b py-1.5 text-[13px] font-black shadow-md transition duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-45',
              roomTheme.primaryBtn
            )}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden /> : null}
            Создать
          </button>
        </div>
      </div>
    </div>
  );
}
